/**
 * Static translations of the editorial CATEGORY labels, keyed by category key (URL segment).
 * Categories are code-first reference data (APA.ADMIN.Services/ReferenceData/ApaCategories.cs —
 * keep both lists in sync), not per-field content, so their labels translate here in code rather
 * than through the AI per-field pipeline. Lookup accepts either the category key ("economie") or
 * the baked French label ("Économie"); anything unknown passes through unchanged, so data that
 * predates a rename keeps rendering.
 */

interface CategoryLabels {
  fr: string;
  en: string;
  ln: string;
}

const CATEGORIES: Record<string, CategoryLabels> = {
  // Structural spine — rendered as the fixed "Articles" crumb, never as a category badge.
  "communique-presse": { fr: "Articles", en: "Articles", ln: "Masolo" },

  economie: { fr: "Économie", en: "Economy", ln: "Ekonomi" },
  "politique-monetaire": { fr: "Politique monétaire", en: "Monetary policy", ln: "Politiki ya mosolo" },
  "marches-financiers": { fr: "Marchés financiers", en: "Financial markets", ln: "Bazando ya misolo" },
  "banques-et-supervision": { fr: "Banques et supervision", en: "Banking and supervision", ln: "Ba banki mpe bokengeli" },
  "monnaie-et-paiements": { fr: "Monnaie et paiements", en: "Currency and payments", ln: "Mbongo mpe bafuti" },
  statistiques: { fr: "Statistiques", en: "Statistics", ln: "Ba statistique" },
  publications: { fr: "Publications et rapports", en: "Publications and reports", ln: "Ba publication mpe ba lapolo" },
  "cooperation-internationale": { fr: "Coopération internationale", en: "International cooperation", ln: "Boyokani ya mikili" },
  institution: { fr: "Vie de l'institution", en: "Inside the institution", ln: "Bomoi ya ebongiseli" },
};

// Reverse index: French label (lowercased) → key, for components that only carry the baked label.
const KEY_BY_FRENCH_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORIES).map(([key, labels]) => [labels.fr.toLowerCase(), key]),
);

function pick(labels: CategoryLabels, locale: string): string {
  if (locale === "en") return labels.en;
  if (locale === "ln") return labels.ln;
  return labels.fr;
}

/**
 * Translate a category label or key for the given locale. `value` may be the category key
 * ("economie"), a key path ("communique-presse/economie" — the last segment wins), or the
 * French display label ("Économie"). Unknown values are returned unchanged.
 */
export function translateCategoryLabel(locale: string, value: string | undefined | null): string {
  const input = (value ?? "").trim();
  if (!input) return "";
  const lastSegment = input.includes("/") ? input.slice(input.lastIndexOf("/") + 1) : input;
  const byKey = CATEGORIES[lastSegment.toLowerCase()];
  if (byKey) return pick(byKey, locale);
  const keyFromLabel = KEY_BY_FRENCH_LABEL[input.toLowerCase()];
  if (keyFromLabel) return pick(CATEGORIES[keyFromLabel], locale);
  return input;
}

/**
 * Translate a baked category PATH label ("Articles > Économie") segment by segment.
 * When the slug key path ("communique-presse/economie") is available each segment
 * translates by key; otherwise each French label segment goes through the same
 * reverse lookup. Unknown segments pass through unchanged.
 */
export function translateCategoryPathLabel(
  locale: string,
  label: string | undefined | null,
  keyPath?: string | undefined | null,
): string {
  const keys = (keyPath ?? "").split("/").map((s) => s.trim()).filter(Boolean);
  if (keys.length > 0) {
    return keys.map((k) => translateCategoryLabel(locale, k)).join(" > ");
  }
  return (label ?? "")
    .split(">")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => translateCategoryLabel(locale, s))
    .join(" > ");
}
